import axios from 'axios';
import type { Router } from 'vue-router';
import TranslationService from '@/locale/translation.service';
import DictService from '@/venit/services/dict/dict.service';
import { useAccountStore } from '@/stores/account';
import { useTranslationStore } from '@/stores/translation';

export default class AccountService {
  constructor(
    private translationService: TranslationService,
    private dictService: DictService,
    private cookie: any,
    private router: Router
  ) {}

  public get authenticated(): boolean {
    const accountStore = useAccountStore();
    return accountStore.authenticated;
  }

  public get userAuthorities(): any {
    const accountStore = useAccountStore();
    return accountStore.account.userRoles;
  }

  public get isAdmin(): boolean {
    return this.authenticated && this.userAuthorities?.includes('ROLE_ADMIN');
  }
  public get isUser(): boolean {
    return this.authenticated && this.userAuthorities?.includes('ROLE_ORGANIZATION_USER');
  }
  public get isRetailUser(): boolean {
    return this.authenticated && this.userAuthorities?.includes('ROLE_RETAIL_USER');
  }
  public get isSales(): boolean {
    return this.authenticated && this.userAuthorities?.includes('ROLE_SALES');
  }
  public get isAuthor(): boolean {
    return this.authenticated && this.userAuthorities?.includes('ROLE_AUTHOR');
  }
  public get isManager(): boolean {
    return this.authenticated && this.userAuthorities?.includes('ROLE_MANAGER');
  }

  public retrieveProfiles(): void {
    const translationStore = useTranslationStore();
    this.retrieveAccount().then(loggedIn => {
      if (loggedIn) {
        this.dictService.readLanguagesList().then(langResponse => {
          translationStore.setupLanguages(langResponse.data.langs);
        });
      }
    });

    // TODO change to api/status
    // axios
    //   .get('management/info')
    //   .then(res => {
    //     if (res.data && res.data.activeProfiles) {
    //       this.store.commit('setRibbonOnProfiles', res.data['display-ribbon-on-profiles']);
    //       this.store.commit('setActiveProfiles', res.data['activeProfiles']);
    //     }
    //     this.retrieveAccount().then(loggedIn => {
    //       if (loggedIn) {
    //         this.dictService.readLanguagesList().then(langResponse => {
    //           this.store.commit('setupLanguages', langResponse.data.langs);
    //         });
    //       }
    //     });
    //   })
    //   .catch(e => {
    //     if (e.response.status == 401) {
    //       console.log('System info not available, user not authorized, should login first');
    //     } else {
    //       console.warn('Read system info error', e.message);
    //     }
    //   });
  }

  public retrieveAccount(): Promise<boolean> {
    const accountStore = useAccountStore();
    const translationStore = useTranslationStore();
    return new Promise(resolve => {
      axios
        .get('api/account')
        .then(response => {
          accountStore.authenticate();
          const account = response.data;
          if (account) {
            accountStore.setAuthenticated(account);
            const userLangKey = account.user.langKey;
            if ((userLangKey && translationStore.currentLanguage !== userLangKey) || !translationStore.currentLanguage) {
              translationStore.setCurrentLanguage(userLangKey);
            }
            if (sessionStorage.getItem('requested-url')) {
              this.router.replace(sessionStorage.getItem('requested-url'));
              sessionStorage.removeItem('requested-url');
            }
            if (this.isUser) {
              accountStore.loadUser();
            }
          } else {
            accountStore.logout();
            this.router.push('/').catch(() => {});
            sessionStorage.removeItem('requested-url');
          }
          this.translationService.refreshTranslation(translationStore.currentLanguage);
          resolve(true);
        })
        .catch(() => {
          accountStore.logout();
          resolve(false);
        });
    });
  }

  public hasAnyAuthorityAndCheckAuth(authorities: any): Promise<boolean> {
    const accountStore = useAccountStore();
    if (typeof authorities === 'string') {
      authorities = [authorities];
    }

    if (!this.authenticated || !this.userAuthorities) {
      // const token = this.cookie.get('JSESSIONID') || this.cookie.get('XSRF-TOKEN');

      // Czy próbować pobrać dane użytkownika, na przykład w przypadku odświeżenia strony
      // mamy cookie (lub token), więc warto sprawdzić, czy mamy zalogowanego użytkownika,
      // zanim każemy mu ponownie się logować.
      // Jak jest używane logowanie z tokenem, to warto sprawdzić czy w sessionStorage są dane.
      // Jeśli logowanie z cookie, to chyba nie ma jak jednoznacznie stwierdzić (httpOnly, secure),
      // więc trzeba tego request-a wysłać.
      const token = true;
      if (!accountStore.account && !accountStore.logon && token) {
        return this.retrieveAccount().then(() => {
          for (let i = 0; i < authorities.length; i++) {
            if (this.userAuthorities?.includes(authorities[i])) {
              return true;
            }
          }
          return false;
        });
      } else {
        return Promise.resolve(false);
      }
    }

    for (let i = 0; i < authorities.length; i++) {
      if (this.userAuthorities.includes(authorities[i])) {
        return Promise.resolve(true);
      }
    }

    return Promise.resolve(false);
  }

  public hasAnyAuthority(authorities: any): boolean {
    if (typeof authorities === 'string') {
      authorities = [authorities];
    }

    if (!this.authenticated || !this.userAuthorities) {
      return false;
    }

    for (const auth in authorities) {
      if (this.userAuthorities.includes(auth)) {
        return true;
      }
    }

    return false;
  }
}
